/**
 * hs-core.js — HighSpark first-party storefront tracker
 * (renamed from hs-tracking.js: "tracking" in filenames/paths is one character
 * away from EasyPrivacy filter patterns; neutral naming keeps the pixel out of
 * adblock lists' generic rules. Endpoint moved to /api/hs/e for the same reason.)
 *
 * Captures UTMs + paid-click IDs (gclid/gbraid/wbraid/fbclid) at landing and
 * gets them onto the Shopify order two ways:
 *   1. DETERMINISTIC (primary): writes them into the cart as cart attributes
 *      via POST /cart/update.js, so they land directly on the order's
 *      note_attributes — the Elevar/Analyzify pattern. Survives GoKwik's
 *      checkout redirect and Safari's 7-day storage purge (written at ATC time,
 *      not read from 90-day storage at conversion).
 *   2. FALLBACK: a first-party event stream to the ERP, joined to the order
 *      server-side by the Shopify `cart` token.
 * Also mirrors GoKwik's own dataLayer events (checkout-initiated / order-placed
 * / gk_purchase) so checkout + purchase signals don't depend on brittle DOM
 * guessing or a Shopify thank-you page that never renders under GoKwik.
 *
 * MASTER SWITCH — DORMANT BY DEFAULT. This script does NOTHING unless
 *   window.HS_TRACKING_ENABLED === true
 * is set before it loads. It can therefore ship to the live theme staged but
 * inert; activate by setting that flag (a one-line theme change) when ready.
 *
 * PURCHASE SELF-FIRE (optional, config-gated): set in theme.liquid
 *   window.HS_GA4_ID      = 'G-XXXXXXXXXX';           // GA4 purchase w/ value
 *   window.HS_ADS_SEND_TO = 'AW-XXXXXXXX/AbCdEfGh';   // Ads valued conversion
 * On GoKwik order success (announced to THIS page before the mobile redirect to
 * the untaggable accounts subdomain) the pixel fires GA4 purchase + Ads
 * conversion with real value + transaction_id itself. Both platforms dedup on
 * transaction_id, so this is safe alongside the desktop channel-pixel firing.
 *
 * Install: add to theme.liquid before </body>
 *   <script>window.HS_TRACKING_ENABLED = true;</script>   // when going live
 *   <script src="{{ 'hs-core.js' | asset_url }}" defer></script>
 *
 * Env: set window.HS_TRACKING_URL to override the ERP endpoint.
 */

(function () {
  "use strict";

  // ── Master switch — inert unless explicitly enabled ─────────────────────────
  if (window.HS_TRACKING_ENABLED !== true) return;

  var ENDPOINT =
    (typeof window.HS_TRACKING_URL === "string" && window.HS_TRACKING_URL) ||
    "https://api.highspark.in/api/hs/e";

  // Capture the pristine fetch up front — used for our own /cart/update.js call
  // so it never re-enters our observer wrapper below.
  var _origFetch = window.fetch;

  var SESSION_TTL_MS = 30 * 60 * 1000; // 30 min inactivity → new session
  var CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000; // Chrome-effective; Safari caps ~7d (mitigated by cart attributes)
  var ANON_KEY = "_hs_anon";
  var SESS_KEY = "_hs_sess";
  var CLICK_KEY = "_hs_click";

  // ── Identity ────────────────────────────────────────────────────────────────

  function uuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function getAnonId() {
    var id = null;
    try { id = localStorage.getItem(ANON_KEY); } catch (_) {}
    if (!id) {
      id = uuid();
      try { localStorage.setItem(ANON_KEY, id); } catch (_) {}
    }
    return id;
  }

  function getSession(utms) {
    var now = Date.now();
    try {
      var raw = sessionStorage.getItem(SESS_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (now - s.ts < SESSION_TTL_MS) {
          s.ts = now;
          s.isNew = false;   /* resumed, not started */
          sessionStorage.setItem(SESS_KEY, JSON.stringify(s));
          return s;
        }
      }
    } catch (_) {}
    var sess = {
      id: uuid(),
      isNew: true,   /* consumed once at init to emit session_start */
      ts: now,
      utm_source: utms.utm_source || null,
      utm_medium: utms.utm_medium || null,
      utm_campaign: utms.utm_campaign || null,
      utm_content: utms.utm_content || null,
      utm_term: utms.utm_term || null,
      landing_page: location.href,
      referrer: document.referrer || null,
    };
    try { sessionStorage.setItem(SESS_KEY, JSON.stringify(sess)); } catch (_) {}
    return sess;
  }

  // ── UTM + click-ID capture ──────────────────────────────────────────────────

  function parseUtms() {
    var params = new URLSearchParams(location.search);
    var keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
    var out = {};
    keys.forEach(function (k) {
      var v = params.get(k);
      if (v) out[k] = v;
    });
    return out;
  }

  function getCookie(name) {
    var m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)"));
    if (!m) return null;
    // decodeURIComponent throws URIError on a malformed %-sequence; a single junk
    // cookie must not kill the whole tracker — fall back to the raw value.
    try { return decodeURIComponent(m[1]); } catch (_) { return m[1]; }
  }

  // Google's Conversion Linker — built into gtag.js, live on this store as
  // AW-10862632638 — persists the last ad click in the first-party _gcl_aw
  // cookie for 90 days, formatted `GCL.<timestampSeconds>.<gclid>`.
  // We previously read ONLY location.search, so a visitor who clicked an ad on
  // Monday and returned on Thursday with a clean URL produced no click id at
  // all, even though Google still knew the click. That is the single largest
  // source of missing attribution (no-click orders average 187 min from session
  // to purchase vs 12 min for click-id orders — i.e. returning buyers).
  function gclidFromCookie() {
    var raw = getCookie("_gcl_aw");
    if (!raw) return null;
    var parts = raw.split(".");
    // GCL . <ts> . <gclid>; rejoin the tail in case a gclid ever contains a dot.
    if (parts.length < 3) return null;
    var id = parts.slice(2).join(".");
    return id || null;
  }

  function captureClick() {
    var params = new URLSearchParams(location.search);
    var gclid = params.get("gclid");
    var gbraid = params.get("gbraid");
    var wbraid = params.get("wbraid");
    var fbclid = params.get("fbclid");
    // srsltid = Merchant Center auto-tagging on FREE listing clicks — the
    // paid-vs-free discriminator (paid Shopping carries gclid instead).
    var srsltid = params.get("srsltid");
    var stored = storedClick();
    if (gclid || gbraid || wbraid || fbclid) {
      // Paid click → full overwrite (last-click-wins).
      stored = {
        gclid: gclid || null,
        gbraid: gbraid || null,
        wbraid: wbraid || null,
        fbclid: fbclid || null,
        srsltid: srsltid || null,
        captured_at: Date.now(),
      };
      try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
    } else if (srsltid && !(stored && (stored.gclid || stored.gbraid || stored.wbraid || stored.fbclid))) {
      // Free-listing click must never clobber a stored PAID click.
      stored = { gclid: null, gbraid: null, wbraid: null, fbclid: null, srsltid: srsltid, captured_at: Date.now() };
      try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
    }
    // No paid click id in THIS url — fall back to Google's own cookie. Strictly
    // additive: a click id we already hold (url or previously stored) always
    // wins, so this can only turn a "no click" into a "click", never rewrite one.
    if (!gclid && !gbraid && !wbraid && !fbclid &&
        !(stored && (stored.gclid || stored.gbraid || stored.wbraid || stored.fbclid))) {
      var cookieGclid = gclidFromCookie();
      if (cookieGclid) {
        stored = {
          gclid: cookieGclid,
          gbraid: null,
          wbraid: null,
          fbclid: null,
          srsltid: (stored && stored.srsltid) || srsltid || null,
          captured_at: Date.now(),
        };
        try { localStorage.setItem(CLICK_KEY, JSON.stringify(stored)); } catch (_) {}
      }
    }
    return stored;
  }

  function storedClick() {
    try {
      var raw = localStorage.getItem(CLICK_KEY);
      if (raw) {
        var s = JSON.parse(raw);
        if (Date.now() - s.captured_at < CLICK_TTL_MS) return s;
      }
    } catch (_) {}
    return null;
  }

  // Meta fbc: prefer the _fbc cookie; else reconstruct fb.1.{captureMs}.{fbclid}
  // per Meta's spec (timestamp = when we first observed the fbclid). fbp cookie.
  function resolveMeta(click) {
    var fbc = getCookie("_fbc");
    if (!fbc && click && click.fbclid) {
      fbc = "fb.1." + (click.captured_at || Date.now()) + "." + click.fbclid;
    }
    return { fbc: fbc || null, fbp: getCookie("_fbp") };
  }

  // The Shopify `cart` cookie value is the cart token; since 2024/25 it carries a
  // `?key=…` suffix. Normalise to the base token (strip at `?`) so the server-side
  // join compares like-for-like against the order's note_attributes cart_token —
  // if either side ever keeps/drops the suffix differently, an exact-match join
  // would silently miss.
  function normalizeCartToken(t) {
    if (!t) return null;
    var s = String(t).trim();
    var q = s.indexOf("?");
    return q >= 0 ? s.slice(0, q) : s;
  }
  function getCartToken() {
    return normalizeCartToken(getCookie("cart"));
  }

  // ── Deterministic identity injection (the primary path) ─────────────────────
  // Write our session + click IDs into the cart as attributes. Shopify carries
  // cart attributes onto the order as note_attributes, and GoKwik preserves them
  // (that is where the order's own cart_token note comes from). The ERP join then
  // reads them DIRECTLY off the order — no probabilistic token lookup, immune to
  // storage purge. hs_ prefix namespaces them.
  var cartAttrsWritten = false;
  function setCartAttributes(force) {
    if (cartAttrsWritten && !force) return;
    var click = storedClick();
    var meta = resolveMeta(click);
    var attrs = { hs_session_id: sess.id, hs_anon_id: anonId };
    if (click) {
      if (click.gclid) attrs.hs_gclid = click.gclid;
      if (click.gbraid) attrs.hs_gbraid = click.gbraid;
      if (click.wbraid) attrs.hs_wbraid = click.wbraid;
      if (click.fbclid) attrs.hs_fbclid = click.fbclid;
      if (click.srsltid) attrs.hs_srsltid = click.srsltid;
      if (click.captured_at) attrs.hs_click_at = String(click.captured_at);
    }
    if (meta.fbc) attrs.hs_fbc = meta.fbc;
    if (meta.fbp) attrs.hs_fbp = meta.fbp;
    if (sess.utm_source) attrs.hs_utm_source = sess.utm_source;
    if (sess.utm_medium) attrs.hs_utm_medium = sess.utm_medium;
    if (sess.utm_campaign) attrs.hs_utm_campaign = sess.utm_campaign;
    if (sess.utm_content) attrs.hs_utm_content = sess.utm_content;
    if (sess.utm_term) attrs.hs_utm_term = sess.utm_term;
    try {
      _origFetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true,
      }).then(function () { cartAttrsWritten = true; }, function () {});
    } catch (_) {}
  }

  // ── Transport ────────────────────────────────────────────────────────────────

  function dynamicFields() {
    var click = storedClick();
    var meta = resolveMeta(click);
    return {
      gclid: (click && click.gclid) || undefined,
      gbraid: (click && click.gbraid) || undefined,
      wbraid: (click && click.wbraid) || undefined,
      fbclid: (click && click.fbclid) || undefined,
      srsltid: (click && click.srsltid) || undefined,
      fbc: meta.fbc || undefined,
      fbp: meta.fbp || undefined,
      click_captured_at: (click && click.captured_at) || undefined,
      cart_token: getCartToken() || undefined,
    };
  }

  function send(payload) {
    // fetch+keepalive (not sendBeacon): the endpoint is cross-origin and the body
    // is application/json, not a CORS-simple type — sendBeacon can't preflight.
    /* ⚠ Body-building is INSIDE the try. It used to sit outside it, which made
       send() capable of throwing synchronously at every call site — and several
       call sites are top-level statements in this IIFE, so one throw would
       abort the rest of module evaluation and silently unregister every
       listener below it, including the cart interceptors and the GoKwik
       purchase self-fire. No current field can actually fail to serialise
       (they are all primitives), so this is closing a structural gap, not a
       live bug — but the file's whole contract is that tracking can never
       break the storefront, and that has to hold for reasons nobody has
       thought of yet. */
    var body;
    try {
      var p = payload;
      if (_uaCh && !_uaChSent) { _uaChSent = true; p = Object.assign({}, payload, { client: Object.assign({}, payload.client || {}, _uaCh) }); }
      body = JSON.stringify(Object.assign({}, p, dynamicFields()));
      _origFetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: body,
        keepalive: true,
        mode: "cors",
        credentials: "omit",
      }).catch(function () {});
    } catch (_) {
      /* Only worth a beacon if the body actually serialised — if JSON.stringify
         was what threw, there is nothing to send. */
      try { if (body && navigator.sendBeacon) navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" })); } catch (__) {}
    }
  }

  /* Device / screen / connection facts for ERP session enrichment (migration 323). Sent on
     session_start; every field optional and bounded server-side; a failure here sends nothing
     extra, never breaks the event. */
  function clientInfo() {
    var c = {};
    try {
      c.vw = window.innerWidth || undefined; c.vh = window.innerHeight || undefined;
      if (window.screen) { c.sw = window.screen.width || undefined; c.sh = window.screen.height || undefined; }
      c.dpr = window.devicePixelRatio || undefined;
      var nc = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (nc) { if (nc.effectiveType) c.net = String(nc.effectiveType).slice(0, 16); if (typeof nc.saveData === "boolean") c.save_data = nc.saveData; }
      if (navigator.language) c.lang = String(navigator.language).slice(0, 35);
      try { var tz = Intl.DateTimeFormat().resolvedOptions().timeZone; if (tz) c.tz = String(tz).slice(0, 64); } catch (_) {}
    } catch (_) {}
    return c;
  }
  /* Chrome freezes the UA string ("Android 10; K"); the real model / OS version come from UA
     Client Hints, which are async. Fetched once per page and attached to the NEXT event as a
     `client` object — the ERP COALESCE-fills them. Only where the API exists (Chromium). */
  var _uaCh = null, _uaChSent = false;
  try {
    if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
      navigator.userAgentData.getHighEntropyValues(["model", "platformVersion"]).then(function (v) {
        if (v && (v.model || v.platformVersion)) _uaCh = { model: v.model ? String(v.model).slice(0, 64) : undefined, pv: v.platformVersion ? String(v.platformVersion).slice(0, 32) : undefined };
      }, function () {});
    }
  } catch (_) {}

  function buildBase(sess, anonId) {
    return {
      anon_id: anonId,
      session_id: sess.id,
      utm_source: sess.utm_source,
      utm_medium: sess.utm_medium,
      utm_campaign: sess.utm_campaign,
      utm_content: sess.utm_content,
      utm_term: sess.utm_term,
      referrer: sess.referrer,
      landing_page: sess.landing_page,
    };
  }

  /* ══ v2 capture helpers ════════════════════════════════════════════════════
     Shared by the retargeting-grade events below. Kept beside buildBase so the
     value-shaping rules (minor units, price banding) live in ONE place — the
     paise/rupee boundary has already cost this codebase a 100x defect once. */

  /* Shopify reports money in MINOR UNITS everywhere in the storefront runtime
     (ShopifyAnalytics variants, /cart/add.js, cart.js). storefront_events.price
     is RUPEES. Divide once, here, and never again downstream. */
  function minorToMajor(minor) {
    var n = typeof minor === "number" ? minor : parseInt(minor, 10);
    return (typeof n === "number" && isFinite(n) && n >= 0) ? n / 100 : null;
  }

  /* Bands, not buckets-of-one. An audience is built on a RANGE, and a band
     survives a price change that would otherwise split one segment in two. */
  function priceBand(rupees) {
    if (typeof rupees !== "number" || !isFinite(rupees) || rupees <= 0) return null;
    if (rupees < 500) return "0-499";
    if (rupees < 1000) return "500-999";
    if (rupees < 2000) return "1000-1999";
    if (rupees < 5000) return "2000-4999";
    return "5000+";
  }

  function variantInfo(vid) {
    try {
      var prod = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
      if (!prod || !prod.variants || !vid) return null;
      for (var i = 0; i < prod.variants.length; i++) {
        if (String(prod.variants[i].id) === String(vid)) return prod.variants[i];
      }
    } catch (_) {}
    return null;
  }

  /* Is this variant buyable? true / false / null (unknown).
     ⚠ NOT from ShopifyAnalytics.meta: its variants carry only id, name, price,
     public_title and sku — there is no `available` field, and meta has no
     selectedVariantId either. Reading them made stock_unavailable impossible:
     measured 2026-10-28, 0 rows EVER against 25,911 variant_select in 30 days,
     while 76 live variants were sold out. The truth comes from
     snippets/hs-variant-availability.liquid, which renders Shopify's own
     `variant.available` for this product server-side into HS_VARIANT_AVAIL. */
  function variantAvailable(vid) {
    try {
      var m = window.HS_VARIANT_AVAIL;
      if (m && vid != null && Object.prototype.hasOwnProperty.call(m, String(vid))) return m[String(vid)] === true;
      var v = variantInfo(vid);
      if (v && typeof v.available === "boolean") return v.available;
    } catch (_) {}
    return null;
  }

  /* One-shot latch per (kind, key) for this PAGE LOAD — not the 30-minute
     tracking session, which lives in `sess`/sessionStorage. A navigation
     resets it, which is intended: the same product viewed on two visits is
     two observations. Engagement signals fire on
     scroll and click, which repeat freely — without this a single restless PDP
     visit could post hundreds of rows. Capped so a pathological page cannot
     grow the latch unboundedly either. */
  var _seen = {}, _seenCount = 0;
  function once(kind, key) {
    var k = kind + "|" + (key || "");
    if (_seen[k]) return false;
    if (_seenCount >= 200) return false;   // hard ceiling per page, fail-closed
    _seen[k] = 1; _seenCount += 1;
    return true;
  }

  /* Consent. ⚠ Deliberately applied ONLY to the identity leg below, never to
     first-party analytics — gating page_view on a privacy API that answers
     `false` by default in some configurations would silently zero the store's
     entire analytics with no error anywhere, which is a worse failure than the
     one it prevents. Analytics behaviour is therefore UNCHANGED by this file;
     only `identify` (which creates a durable marketing identity) consults it. */
  function marketingConsentOk() {
    try {
      if (window.HS_TRACKING_CONSENT === false) return false;
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.marketingAllowed === "function") return api.marketingAllowed() === true;
    } catch (_) {}
    return false;   // fail-closed: absent/unknown consent means NO identity capture
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  captureClick(); // persist any gclid/fbclid on this landing URL before first send
  var utms = parseUtms();
  var sess = getSession(utms);
  var anonId = getAnonId();
  var base = buildBase(sess, anonId);

  send(Object.assign({}, base, { event_type: "page_view", page_url: location.href }));

  /* session_start — declared in the allowlist since 044 and never once fired.
     Without it a session's first touch is indistinguishable from its tenth, so
     "how many sessions" has to be derived by grouping page_views. */
  if (sess.isNew) {
    send(Object.assign({}, base, { event_type: "session_start", page_url: location.href, client: clientInfo() }));
  }

  var metaObj = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
  var pageObj = metaObj && metaObj.page;
  var pageType = pageObj && pageObj.pageType;

  /* collection_view and search were both allowlisted and DB-accepted from day
     one and have ZERO rows to this day — the vocabulary existed, nothing spoke
     it. Shopify populates ShopifyAnalytics.meta.page on every storefront page,
     so both are a read, not an integration. */
  if (pageType === "collection") {
    var collExtra = { event_type: "collection_view", page_url: location.href, metadata: {} };
    if (pageObj.resourceId != null) collExtra.metadata.collection_id = String(pageObj.resourceId);
    /* The handle is the human-readable key and survives a collection being
       renamed in admin, which the id does not. */
    var m = location.pathname.match(/\/collections\/([^/?#]+)/);
    if (m) collExtra.metadata.handle = m[1].slice(0, 255);
    send(Object.assign({}, base, collExtra));
  }

  /* ⚠ Shopify's pageType for /search is "searchresults", not "search" — this branch never fired
     (0 search rows ever; 527 searchresults events on /search URLs in one day, audit 2026-10-23). */
  if (pageType === "search" || pageType === "searchresults") {
    var term = "";
    try { term = new URLSearchParams(location.search).get("q") || ""; } catch (_) {}
    /* ⚠ A search term is shopper-authored free text — people type things into
       a search box that they would not put in a form. It stays in our own
       first-party store, is capped at 120 chars, and must NOT be forwarded to
       any ad platform without a separate decision. Empty searches are skipped:
       they are navigation, not demand. */
    if (term) {
      send(Object.assign({}, base, {
        event_type: "search",
        page_url: location.href,
        metadata: { q: String(term).slice(0, 120) },
      }));
    }
  }

  /* ── product_view, enriched ────────────────────────────────────────────────
     ⚠ THE JOIN BRIDGE. Measured on prod 2026-09-12: of 115,230 product_view
     rows in 30 days, ZERO carried a `sku` and ZERO carried a `price` — only
     Shopify's own product/variant ids. Our catalogue is keyed on
     `products.master_sku`, and there is no reliable live table mapping a
     Shopify product id to it (the variant snapshot is a one-off 2026-06-02
     dump). So a product view could not be joined to the ERP at all, which is
     what blocked every "what did they browse" audience and every view-vs-sales
     demand question.

     `ShopifyAnalytics.meta.product.variants[]` already carries `sku`, `price`
     and `public_title` on every storefront page. This is a READ, not an
     integration. ⚠ `price` there is in MINOR UNITS (paise) exactly like
     /cart/add.js — same boundary that produced the 100x ad-ledger defect. */
  /* Wrapped like every other emission in this file. The sibling
     `landed_sold_out` check below already does this; leaving a top-level
     block unguarded is the inconsistency an audit caught. */
  try {
    if (metaObj && metaObj.product) {
      var pvVid = String((metaObj.selectedVariantId || (metaObj.product.variants && metaObj.product.variants[0] && metaObj.product.variants[0].id)) || "");
      var pvV = variantInfo(pvVid);
      var pvEvt = {
        event_type: "product_view",
        page_url: location.href,
        product_id: String(metaObj.product.id || ""),
        product_title: metaObj.product.title || "",
        variant_id: pvVid,
        metadata: {},
      };
      if (pvV) {
        if (pvV.sku) pvEvt.sku = String(pvV.sku).slice(0, 128);
        var pvRupees = minorToMajor(pvV.price);
        if (pvRupees != null) {
          pvEvt.price = pvRupees;
          /* A price BAND, not just the number: audiences are built on ranges
             ("showed intent above Rs.2000"), and a band survives a price change
             that would otherwise fragment the segment. */
          var b = priceBand(pvRupees);
          if (b) pvEvt.metadata.price_band = b;
        }
        /* The variant's own option label — for jewellery this is the single most
           useful segmentation attribute there is: silver vs gold vs rose gold is
           a different buyer, a different price point and a different ad. */
        if (pvV.public_title) pvEvt.metadata.variant_title = String(pvV.public_title).slice(0, 120);
      }
      var pvHandle = location.pathname.match(/\/products\/([^/?#]+)/);
      if (pvHandle) pvEvt.metadata.handle = pvHandle[1].slice(0, 255);
      if (metaObj.product.type) pvEvt.metadata.product_type = String(metaObj.product.type).slice(0, 120);
      if (metaObj.product.vendor) pvEvt.metadata.vendor = String(metaObj.product.vendor).slice(0, 120);
      send(Object.assign({}, base, pvEvt));
    }
  } catch (_) { /* tracking must never break the storefront */ }

  /* ══════════════════════════════════════════════════════════════════════════
     RETARGETING-GRADE CAPTURE (v2)

     What this is for: today an audience can only be described as "visited the
     site" or "added to cart". Neither says WHAT the person wanted, so every
     retargeting ad is the same ad. These events describe the INTENT — which
     metal, which price band, which item they chose off a grid, what they tried
     to buy and could not, and how deeply they engaged before leaving.

     ⚠ EVERY field here goes into columns that already exist (`product_id`,
     `variant_id`, `sku`, `price`, `quantity`) or into `metadata` (jsonb, hard
     4096-byte cap enforced at the write). NO new column, NO schema change —
     the only DB change in this PR is widening the event_type CHECK, which must
     ship in the SAME PR as the route allowlist (the ONE CONTRACT rule; ship
     one without the other and the route answers 200 while the INSERT is
     swallowed by its fail-soft try/catch).

     ⚠ Nothing here captures contact details. The one event that touches
     identity (`identify`) sends a SHA-256 hash and is fail-closed OFF. */

  // ── view_cart ──────────────────────────────────────────────────────────────
  /* The missing middle of the funnel. We know who added and who ordered, and
     nothing about the gap: a cart opened and abandoned is a fundamentally
     different person from one who added and never looked again — the first has
     a price/shipping objection, the second forgot. They deserve different ads. */
  function emitViewCart(trigger) {
    if (!once("view_cart", trigger)) return;
    send(Object.assign({}, base, {
      event_type: "view_cart",
      page_url: location.href,
      metadata: { trigger: String(trigger || "").slice(0, 60) },
    }));
  }
  try { if (pageType === "cart") emitViewCart("cart_page"); } catch (_) {}

  // ── select_item ────────────────────────────────────────────────────────────
  /* Which item was chosen OFF A GRID, and out of what list. A click from a
     "you may also like" row is a merchandising win; the same click from search
     is a findability win. Today both are invisible and the PDP view that
     follows cannot tell you where it came from. */
  function listContext(el) {
    try {
      var sec = el.closest && el.closest("[data-section-type], section[id], .collection, predictive-search, product-recommendations");
      if (sec) {
        if (sec.tagName === "PRODUCT-RECOMMENDATIONS") return "recommendations";
        if (sec.tagName === "PREDICTIVE-SEARCH") return "predictive_search";
        var dt = sec.getAttribute && sec.getAttribute("data-section-type");
        if (dt) return String(dt).slice(0, 60);
        var sid = sec.getAttribute && sec.getAttribute("id");
        if (sid) return String(sid).slice(0, 60);
      }
    } catch (_) {}
    if (pageType === "collection") return "collection";
    if (pageType === "search" || pageType === "searchresults") return "search";
    return "other";
  }
  document.addEventListener("click", function (e) {
    try {
      var t = e.target;
      if (!t || !t.closest) return;
      var a = t.closest('a[href*="/products/"]');
      if (a) {
        var href = a.getAttribute("href") || "";
        var mh = href.match(/\/products\/([^/?#]+)/);
        if (mh && once("select_item", mh[1])) {
          var meta = { handle: mh[1].slice(0, 255), list: listContext(a) };
          var mv = href.match(/[?&]variant=(\d+)/);
          var ev = { event_type: "select_item", page_url: location.href, metadata: meta };
          if (mv) ev.variant_id = mv[1];
          send(Object.assign({}, base, ev));
        }
      }
      /* Cart drawer opens from a link/button to /cart that the theme
         intercepts — the navigation never happens, so a page_view will not
         record it. */
      var c = t.closest('a[href$="/cart"], a[href*="/cart?"], [id*="cart-icon"], [class*="cart-icon"]');
      if (c) emitViewCart("cart_drawer");
      /* Engagement: an expandable detail block (size guide, care, shipping,
         authenticity). WHICH one is opened is the interesting part. */
      var sum = t.closest("summary, [data-accordion-trigger]");
      if (sum) {
        var label = (sum.textContent || "").trim().replace(/\s+/g, " ").slice(0, 60);
        if (label) emitEngagement("detail_open", label);
      }
      /* Engagement: product gallery / zoom interaction. For jewellery this is
         the trust signal — people zoom to inspect the stone, clasp and finish.
         External research ties visualisation engagement to both purchase rate
         and RETURN rate, and we have never measured it. */
      var g = t.closest(".product__media, .product__modal-opener, [data-gallery], .product-media-modal");
      if (g && metaObj && metaObj.product) emitEngagement("media_zoom", String(metaObj.product.id || ""));
    } catch (_) { /* tracking must never break the storefront */ }
  }, true);

  // ── variant_select + stock_unavailable ─────────────────────────────────────
  /* Jewellery's defining segmentation axis. A shopper who picked ROSE GOLD at
     Rs.2,400 is a different audience from one who picked silver at Rs.700, and
     today both land in the same undifferentiated "viewed a product" bucket.

     stock_unavailable is the other half and is the more valuable one: it is
     demand we could not serve. It never reaches `add_to_cart`, never reaches a
     sale, and is therefore invisible to every sales-derived forecast we run —
     including the reorder model, which computes demand from sales ONLY and
     currently has a real lead time for 0 of 3,321 SKUs. */
  function emitVariant(vid, reason) {
    var v = variantInfo(vid);
    /* `reason` is set HERE, while meta is still a standalone object. It used to
       be assigned after `evt.metadata = meta`, which worked only because the
       two are the same reference — correct, but by accident: a later
       defensive-copy refactor would have dropped the field from every row with
       nothing failing. Build the object fully, then attach it. */
    var meta = { reason: String(reason || "").slice(0, 40) };
    if (v && v.public_title) meta.variant_title = String(v.public_title).slice(0, 120);
    var rupees = v ? minorToMajor(v.price) : null;
    if (rupees != null) { var b = priceBand(rupees); if (b) meta.price_band = b; }
    var soldOut = variantAvailable(vid) === false;
    var evt = {
      event_type: soldOut ? "stock_unavailable" : "variant_select",
      page_url: location.href,
      variant_id: String(vid),
      metadata: meta,
    };
    if (metaObj && metaObj.product) {
      evt.product_id = String(metaObj.product.id || "");
      evt.product_title = metaObj.product.title || "";
    }
    if (v && v.sku) evt.sku = String(v.sku).slice(0, 128);
    if (rupees != null) evt.price = rupees;
    if (!once(evt.event_type, String(vid))) return;
    send(Object.assign({}, base, evt));
  }
  /* Dawn writes the chosen variant into a hidden [name="id"] input on every
     option change — one listener covers radios, dropdowns and swatches. */
  document.addEventListener("change", function (e) {
    try {
      var t = e.target;
      if (!t) return;
      if (t.name === "id" && t.value) { emitVariant(t.value, "variant_input"); return; }
      if (t.closest && t.closest("variant-radios, variant-selects, .product-form__input")) {
        var hidden = document.querySelector('form[action*="/cart/add"] [name="id"]');
        if (hidden && hidden.value) emitVariant(hidden.value, "option_change");
      }
    } catch (_) {}
  }, true);
  /* On load: if the product lands already sold out, that is unmet demand too —
     and it is the case a change-listener can never see. */
  try {
    if (metaObj && metaObj.product) {
      /* The selected variant: the snippet's server-rendered id first (Shopify's
         selected_or_first_available_variant — honours ?variant=), then the
         product form's hidden input, which Dawn fills with the same id. */
      var idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
      var loadVid = String(window.HS_SELECTED_VARIANT_ID || (idInput && idInput.value) || metaObj.selectedVariantId || "");
      if (loadVid && variantAvailable(loadVid) === false) emitVariant(loadVid, "landed_sold_out");
    }
  } catch (_) {}

  // ── engagement ─────────────────────────────────────────────────────────────
  /* Depth, not just presence. Separates "bounced off an ad" from "read the
     whole page twice" — which is exactly the distinction that decides whether
     someone is worth bidding on again, and it is the pre-checkout context
     GoKwik's own RTO model cannot see because it only meets the shopper at
     checkout. */
  function emitEngagement(kind, detail) {
    if (!once("engagement", kind + "|" + (detail || ""))) return;
    var meta = { kind: String(kind).slice(0, 40) };
    if (detail) meta.detail = String(detail).slice(0, 120);
    if (pageType) meta.page_type = String(pageType).slice(0, 40);
    var evt = { event_type: "engagement", page_url: location.href, metadata: meta };
    if (metaObj && metaObj.product) evt.product_id = String(metaObj.product.id || "");
    send(Object.assign({}, base, evt));
  }
  /* Public hook (2026-10-23) so a page script can record a NAMED action — the
     account page uses it ("acct_click" + the action, e.g. "invoice"). Same
     event type, same once() de-dupe and page cap; detail is capped at 120. */
  window.hsEngage = function (kind, detail) { try { emitEngagement(kind, detail); } catch (_) {} };
  /* Scroll depth, sampled at milestones rather than continuously — a scroll
     handler that sends is a scroll handler that floods. */
  (function () {
    var marks = [50, 90], hit = {};
    function onScroll() {
      try {
        var h = document.documentElement;
        var denom = (h.scrollHeight - h.clientHeight);
        if (denom <= 0) return;
        var pct = Math.round(((window.pageYOffset || h.scrollTop) / denom) * 100);
        for (var i = 0; i < marks.length; i++) {
          if (pct >= marks[i] && !hit[marks[i]]) { hit[marks[i]] = 1; emitEngagement("scroll_" + marks[i], pageType || ""); }
        }
        if (hit[50] && hit[90]) window.removeEventListener("scroll", onScroll);
      } catch (_) {}
    }
    window.addEventListener("scroll", onScroll, { passive: true });
  })();
  /* The EDD pin-code widget is a volunteered geography + delivery-intent
     signal, and checking delivery is one of the strongest late-funnel actions
     on the page. The PIN itself is an area, not an address — but it is still
     personal data, so it is stored coarsely (first 3 digits = region) and
     never forwarded anywhere. */
  document.addEventListener("submit", function (e) {
    try {
      var f = e.target;
      if (!f || !f.querySelector) return;
      var pin = f.querySelector('[name*="pincode" i], [id*="pincode" i], [name*="pin_code" i]');
      if (pin && pin.value) {
        var v = String(pin.value).replace(/\D/g, "");
        emitEngagement("pincode_check", v ? v.slice(0, 3) + "xxx" : "");
      }
    } catch (_) {}
  }, true);

  // ── identify ───────────────────────────────────────────────────────────────
  /* ⚠⚠ THE ONLY EVENT HERE THAT TOUCHES IDENTITY, AND IT SHIPS OFF.
     Reachability is the real ceiling on retargeting: measured, only ~3% of
     anon_ids ever resolve to a contactable person site-wide (23% among
     wishlisters). Capturing a hashed identifier at the moment someone
     volunteers it is what raises that number.

     Three fences, all of which must pass:
       1. `window.HS_IDENTITY_CAPTURE === true` — absent by default, so this is
          INERT as shipped and a deliberate act to enable.
       2. `marketingConsentOk()` — fail-CLOSED: unknown consent means no.
       3. Only a SHA-256 hash ever leaves the browser. The raw value is never
          sent, never stored, and never logged. This is also the exact form
          Google and Meta want, so nothing is lost by never holding the plain
          text.
     There is no cookie-consent banner on this storefront today. That is the
     reason fence 1 exists and why this must not be switched on before one. */
  function sha256Hex(str) {
    try {
      if (!window.crypto || !window.crypto.subtle) return Promise.resolve(null);
      var buf = new TextEncoder().encode(str);
      return window.crypto.subtle.digest("SHA-256", buf).then(function (d) {
        var a = Array.prototype.slice.call(new Uint8Array(d));
        return a.map(function (b) { return ("00" + b.toString(16)).slice(-2); }).join("");
      }).catch(function () { return null; });
    } catch (_) { return Promise.resolve(null); }
  }
  function identify(rawValue, kind, method) {
    try {
      if (window.HS_IDENTITY_CAPTURE !== true) return;
      if (!marketingConsentOk()) return;
      var v = String(rawValue || "").trim().toLowerCase();
      if (kind === "phone") v = v.replace(/\D/g, "").slice(-10);   // India: last 10 digits
      if (!v || (kind === "email" && v.indexOf("@") === -1)) return;
      if (!once("identify", kind + "|" + method)) return;
      sha256Hex(v).then(function (hex) {
        if (!hex) return;
        send(Object.assign({}, base, {
          event_type: "identify",
          page_url: location.href,
          metadata: { kind: kind, method: String(method || "").slice(0, 40), hash: hex },
        }));
      });
    } catch (_) {}
  }
  document.addEventListener("submit", function (e) {
    try {
      var f = e.target;
      if (!f || !f.querySelector) return;
      var em = f.querySelector('input[type="email"]');
      if (em && em.value) identify(em.value, "email", f.getAttribute("action") ? "form" : "newsletter");
      var ph = f.querySelector('input[type="tel"]');
      if (ph && ph.value) identify(ph.value, "phone", "form");
    } catch (_) {}
  }, true);
  /* KwikPass fires its own login event; wishlist.js already bridges the prompt.
     A logged-in customer id is not a secret and is the cheapest durable link
     between this anon_id and a real person we can already reach. */
  document.addEventListener("hs:identity", function (e) {
    try {
      var d = (e && e.detail) || {};
      if (d.email) identify(d.email, "email", d.method || "kwikpass");
      if (d.phone) identify(d.phone, "phone", d.method || "kwikpass");
    } catch (_) {}
  });

  // ── Wishlist observation ────────────────────────────────────────────────────
  // wishlist.js ALREADY dispatches `hs:wishlist:change` on every toggle — for
  // guests and logged-in customers alike — so this only has to listen. The
  // wishlist file is deliberately untouched: the tracking concern stays in the
  // tracking file, and a bug here cannot break saving.
  //
  // Why these events exist at all: `storefront_wishlists` persists ONLY
  // logged-in customers (its App-Proxy sync route needs a signed
  // logged_in_customer_id), and with Shopify's new customer accounts almost
  // nobody signs in — so that table measured sign-in rate, not wishlist use. It
  // held 3 rows from 1 customer while the hearts were, in all likelihood, being
  // used far more. These events key on anon_id, which every visitor already has
  // from the pixel, so guest hearts become countable without asking anyone to
  // log in and without storing anything new about them.
  //
  // Measurement only: an anon_id already sent on every page_view, plus a
  // product handle. No identity, no contact details.
  /* ── Wishlist sign-in prompt ────────────────────────────────────────────
     A PAIR, and the pair is the point. Counting clicks alone cannot separate
     "nobody wanted to sign in" from "the button never rendered", and those call
     for opposite responses. That is not hypothetical here: the prompt shipped
     2026-09-03 gated on `__KP_LOGIN_SDK_INSTANCE__`, a global from GoKwik's
     HEADLESS integration guide that does not exist on a Shopify app install, so
     it was invisible for a week while the fail-quiet design reported nothing.
     `shown` is the denominator that makes `click` mean something.

     wishlist.js dispatches; this file decides what to send. */
  document.addEventListener("hs:wishlist:signin", function (e) {
    try {
      var phase = e && e.detail && e.detail.phase;
      /* Map explicitly — the event_type must never be built by interpolating a
         value from the page, because an unknown type is refused by BOTH the
         route allowlist and the DB CHECK. */
      var type = phase === "shown" ? "wishlist_signin_shown"
               : phase === "click" ? "wishlist_signin_click"
               : null;
      if (!type) return;
      send(Object.assign({}, base, {
        event_type: type,
        page_url: location.href,
      }));
    } catch (_) { /* tracking must never break the storefront */ }
  });

  document.addEventListener("hs:wishlist:change", function (e) {
    try {
      var d = (e && e.detail) || {};
      // Map explicitly rather than interpolating the op into the event name —
      // the event_type must never be attacker- or typo-controlled, because an
      // unknown type is rejected by the route allowlist AND the DB CHECK.
      var type = d.op === "add" ? "wishlist_add" : d.op === "remove" ? "wishlist_remove" : null;
      if (!type) return;
      var handle = typeof d.handle === "string" ? d.handle.slice(0, 255) : "";
      if (!handle) return;
      var meta = { handle: handle };
      // List size after the change — lets us see depth of use (1 heart vs 12)
      // without storing the list itself.
      if (d.handles && typeof d.handles.length === "number") meta.list_size = d.handles.length;
      send(Object.assign({}, base, {
        event_type: type,
        page_url: location.href,
        metadata: meta,
      }));
    } catch (_) { /* tracking must never break the storefront */ }
  });

  // ── Add-to-cart observation (fetch + XHR) ───────────────────────────────────
  // Observe-only: always delegates to the original, never alters the request.

  function isCartAddUrl(u) { return String(u || "").indexOf("/cart/add") !== -1; }
  /* Shopify removes a line by SETTING ITS QUANTITY TO ZERO via /cart/change —
     there is no delete endpoint — so a removal is only distinguishable from an
     ordinary quantity edit by reading the requested quantity. Without this,
     "added then removed" (a rejected product) is indistinguishable from "added
     and left in the cart" (an abandoned one); they are different problems. */
  function isCartChangeUrl(u) {
    var v = String(u || "");
    return v.indexOf("/cart/change") !== -1 || v.indexOf("/cart/update") !== -1;
  }
  function parseRemoval(body) {
    try {
      var id = null, qty = null;
      if (typeof body === "string") {
        var t = body.trim();
        if (t.charAt(0) === "{") {
          var j = JSON.parse(t);
          if (j && j.updates && typeof j.updates === "object") {
            /* /cart/update takes {updates:{<variantOrLineKey>: qty}} — a single
               request can zero several lines at once. */
            var zeroed = [], changed = [];
            if (!Array.isArray(j.updates)) {
              for (var k in j.updates) {
                if (!Object.prototype.hasOwnProperty.call(j.updates, k)) continue;
                /* removal gate stays exactly "updated quantity is 0" (pinned by the parity guard) */
                if (Number(j.updates[k]) === 0) zeroed.push(String(k));
                else if (isFinite(Number(j.updates[k])) && Number(j.updates[k]) > 0) changed.push({ id: String(k), qty: Number(j.updates[k]) });
              }
            }
            return zeroed.length || changed.length ? { ids: zeroed, changes: changed } : null;
          }
          id = j && (j.id != null ? j.id : (j.line != null ? "#line:" + j.line : null)); qty = j && j.quantity;
        } else {
          var sp = new URLSearchParams(t);
          id = sp.get("id") || (sp.get("line") ? "#line:" + sp.get("line") : null); qty = sp.get("quantity");
        }
      } else if (typeof FormData !== "undefined" && body instanceof FormData) {
        id = body.get("id") || (body.get("line") ? "#line:" + body.get("line") : null); qty = body.get("quantity");
      } else if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        id = body.get("id") || (body.get("line") ? "#line:" + body.get("line") : null); qty = body.get("quantity");
      }
      if (id != null && Number(qty) === 0) return { ids: [String(id)], changes: [] };
      if (id != null && qty != null && isFinite(Number(qty)) && Number(qty) > 0) return { ids: [], changes: [{ id: String(id), qty: Number(qty) }] };
    } catch (_) {}
    return null;
  }
  /* Line-key → product cache, filled from Shopify's OWN cart responses (/cart/add returns the
     created line incl. `key`; /cart/change and /cart/update return the whole cart). Kept in
     sessionStorage (≤ 60 lines) so a removal on a later page still knows WHAT was removed.
     Measured before: 129 of 129 remove_from_cart rows in a day had no variant and no SKU —
     GoKwik's side cart removes by line key. */
  var LINE_CACHE_KEY = "_hs_lines";
  function readLineCache() { try { return JSON.parse(sessionStorage.getItem(LINE_CACHE_KEY) || "{}") || {}; } catch (_) { return {}; } }
  function rememberLines(items, isFullCart) {
    try {
      if (!items || !items.length) return;
      var c = readLineCache(), keys = Object.keys(c);
      /* Line POSITIONS are only true for the cart they were read from. An add shifts them, so on
         anything but a full-cart response drop every cached position: a {line:n} edit then
         carries line_index alone rather than the WRONG product (re-audit 2026-10-23). */
      if (!isFullCart) for (var lk in c) if (Object.prototype.hasOwnProperty.call(c, lk) && lk.indexOf("#line:") === 0) delete c[lk];
      for (var i = 0; i < items.length && i < 50; i++) {
        var it = items[i]; if (!it) continue;
        var rec = {
          v: it.variant_id != null ? String(it.variant_id) : (it.id != null ? String(it.id) : null),
          p: it.product_id != null ? String(it.product_id) : null,
          s: typeof it.sku === "string" && it.sku ? it.sku.slice(0, 128) : null,
          pr: typeof it.final_price === "number" ? it.final_price / 100 : (typeof it.price === "number" ? it.price / 100 : null),
          t: (it.product_title || it.title || "").slice(0, 120) || null,
          q: typeof it.quantity === "number" ? it.quantity : null,
        };
        if (it.key) c[String(it.key).slice(0, 120)] = rec;
        if (rec.v) c[rec.v] = rec;
        // /cart/change {line: n} addresses the n-th line (1-based) of the CURRENT cart.
        if (isFullCart) c["#line:" + (i + 1)] = rec;
      }
      keys = Object.keys(c);
      if (keys.length > 60) for (var k = 0; k < keys.length - 60; k++) delete c[keys[k]];
      sessionStorage.setItem(LINE_CACHE_KEY, JSON.stringify(c));
    } catch (_) {}
  }
  function lookupLine(id) { var c = readLineCache(); return c[String(id)] || null; }
  function lineFields(e, id) {
    var hit = lookupLine(id);
    var li = /^#line:(\d{1,4})$/.exec(String(id));
    if (li) e.metadata = Object.assign({}, e.metadata || {}, { line_index: Number(li[1]) });   // a POSITION, never a variant
    else if (/^\d{1,19}$/.test(id)) e.variant_id = id;
    else e.metadata = Object.assign({}, e.metadata || {}, { line_key: String(id).slice(0, 120) });
    if (hit) {
      if (hit.v && !e.variant_id) e.variant_id = hit.v;
      if (hit.p) e.product_id = hit.p;
      if (hit.s) e.sku = hit.s;
      if (hit.pr != null && isFinite(hit.pr)) e.price = hit.pr;
      if (hit.t) e.product_title = hit.t;
    }
    return hit;
  }
  function onCartRemove(ids) {
    if (!ids || !ids.length) return;
    for (var i = 0; i < ids.length && i < 20; i++) {
      var e = { event_type: "remove_from_cart", page_url: location.href, quantity: 0 };
      /* A /cart/change id may be a variant id OR a line key like "4321:abc…" — only the numeric
         form is a variant; a line key is resolved through the cache above. */
      lineFields(e, ids[i]);
      send(Object.assign({}, base, e));
    }
  }
  /* Non-zero quantity edit (+/- in the cart): cart_update with the NEW quantity and the previous
     one when known. Before, only quantity 0 (a removal) was recorded. */
  function onCartUpdate(changes) {
    if (!changes || !changes.length) return;
    for (var i = 0; i < changes.length && i < 20; i++) {
      var e = { event_type: "cart_update", page_url: location.href, quantity: changes[i].qty };
      var hit = lineFields(e, changes[i].id);
      if (hit && typeof hit.q === "number") e.metadata = Object.assign({}, e.metadata || {}, { prev_quantity: hit.q });
      send(Object.assign({}, base, e));
      // Update the cached quantity NOW — the response refresh is async, so rapid +taps read stale.
      try { var c = readLineCache(); var k = String(changes[i].id); if (c[k]) { c[k].q = changes[i].qty; sessionStorage.setItem(LINE_CACHE_KEY, JSON.stringify(c)); } } catch (_) {}
    }
  }
  function parseAddBody(body) {
    var out = { variantId: null, qty: 1 };
    function take(id, q) { if (id) { out.variantId = String(id); out.qty = parseInt(q || "1", 10) || 1; } }
    try {
      if (typeof body === "string") {
        var t = body.trim();
        if (t.charAt(0) === "{") { var parsed = JSON.parse(t); if (parsed && parsed.id) take(parsed.id, parsed.quantity); }
        else { var sp = new URLSearchParams(t); take(sp.get("id"), sp.get("quantity")); }
      } else if (typeof FormData !== "undefined" && body instanceof FormData) {
        take(body.get("id"), body.get("quantity"));
      } else if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        take(body.get("id"), body.get("quantity"));
      }
    } catch (_) {}
    return out;
  }
  /* Shopify's /cart/add.js answers with the line item(s) it actually created:
     product_id, sku, price, title — everything the REQUEST body does not carry.
     Measured before this was added: of 12,209 add_to_cart rows in 30 days,
     ZERO had product_id/sku/price and only 39% had even a variant_id, because
     the request body carries `id` (a variant) and nothing else. The event
     recorded THAT something was added, never WHAT — useless for "added but
     never bought", which is the whole question it exists to answer.

     ⚠ price arrives in MINOR UNITS (paise). `storefront_events.price` is rupees
     — the same paise/rupee boundary that produced a 100x defect in the ad
     conversion ledger. Divide once, here, and never again downstream. */
  function normaliseAddedItems(body) {
    var raw = [];
    if (body && Array.isArray(body.items)) raw = body.items;          // {items:[…]} multi-add
    else if (body && (body.id || body.variant_id)) raw = [body];      // single add → the line item itself
    var out = [];
    for (var i = 0; i < raw.length && i < 20; i++) {
      var it = raw[i]; if (!it) continue;
      var minor = (typeof it.final_price === "number") ? it.final_price : it.price;
      out.push({
        variant_id: it.variant_id != null ? String(it.variant_id) : (it.id != null ? String(it.id) : null),
        product_id: it.product_id != null ? String(it.product_id) : null,
        sku: typeof it.sku === "string" && it.sku ? it.sku : null,
        price: typeof minor === "number" && isFinite(minor) ? minor / 100 : null,
        quantity: typeof it.quantity === "number" ? it.quantity : 1,
        product_title: it.product_title || it.title || null,
      });
    }
    return out;
  }

  function onCartAdd(variantId, qty, addedItems) {
    // The `cart` cookie is set by the /cart/add response, so run this AFTER it
    // resolves. Write the deterministic cart attributes now that a cart exists,
    // and emit the fallback event (cart_token now available via dynamicFields).
    setCartAttributes(false);
    var items = addedItems && addedItems.length ? addedItems : null;
    if (items) {
      /* One event per line item — a multi-add is genuinely several adds, and
         collapsing them would make per-SKU analysis wrong in the one case that
         matters (bundles). Capped at 20 upstream so a pathological payload
         cannot flood the pipeline. */
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var e = { event_type: "add_to_cart", page_url: location.href, quantity: it.quantity };
        if (it.variant_id) e.variant_id = it.variant_id;
        if (it.product_id) e.product_id = it.product_id;
        if (it.sku) e.sku = it.sku;
        if (it.price != null) e.price = it.price;
        if (it.product_title) e.product_title = it.product_title;
        send(Object.assign({}, base, e));
      }
      return;
    }
    /* Fallback: the response was unreadable (opaque, non-JSON, a shape we do
       not know). Emit the pre-enrichment event rather than nothing — a
       thinner row still beats a hole in the funnel. */
    var extra = { event_type: "add_to_cart", page_url: location.href, quantity: qty || 1 };
    if (variantId) extra.variant_id = variantId;
    send(Object.assign({}, base, extra));
  }

  window.fetch = function (input, init) {
    // method/url may live on a Request object OR in init.
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var method = (init && init.method) || (input && input.method) || "GET";
    var isAdd = isCartAddUrl(url) && String(method).toUpperCase() === "POST";
    var isChange = !isAdd && isCartChangeUrl(url) && String(method).toUpperCase() === "POST";
    var parsed = isAdd ? parseAddBody(init && init.body) : null;
    var removal = isChange ? parseRemoval(init && init.body) : null;
    var ret = _origFetch.apply(this, arguments);
    if (removal && ret && typeof ret.then === "function") {
      /* Count only a 2xx. Emit from the PRE-change cache (a removed line is gone from the
         response), then refresh the cache from a CLONE of the response — never the original. */
      ret.then(function (r) {
        if (!(r && r.ok)) return;
        onCartRemove(removal.ids); onCartUpdate(removal.changes);
        try { r.clone().json().then(function (b) { if (b && Array.isArray(b.items)) rememberLines(b.items, true); }, function () {}); } catch (_) {}
      }, function () {});
    }
    if (isAdd && ret && typeof ret.then === "function") {
      // Count SUCCESSFUL adds only. fetch resolves on HTTP errors too — a 422
      // (sold out / quantity rule) previously fired add_to_cart, and so did the
      // rejection path (network failure = no cart mutation at all). Both were
      // polluting the funnel in storefront_events. Only .ok/.status are read —
      // never the body — so the page's own response handling is untouched.
      ret.then(function (response) {
        if (!response || !response.ok) return;
        /* ⚠ CLONE. A Response body is a single-use stream: reading it here
           would leave the storefront's own .json() with an empty stream and
           break the cart drawer. clone() gives us an independent copy and
           leaves the original untouched — this is the ONLY safe way to read a
           body from an interceptor, and the reason the previous version
           deliberately read nothing but .ok. If clone/json fails for any
           reason we still emit the thin event. */
        var done = false;
        function emit(items) { if (!done) { done = true; onCartAdd(parsed.variantId, parsed.qty, items); } }
        try {
          response.clone().json().then(
            function (body) {
              try { rememberLines(body && Array.isArray(body.items) ? body.items : (body ? [body] : [])); } catch (_) {}
              emit(normaliseAddedItems(body));
            },
            function () { emit(null); }
          );
        } catch (_) { emit(null); }
      }, function () { /* network failure: nothing was added, count nothing */ });
    }
    return ret;
  };

  // Older / jQuery cart drawers post /cart/add.js via XHR — observe those too.
  try {
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var _open = XHR.prototype.open;
      var _sendX = XHR.prototype.send;
      XHR.prototype.open = function (m, u) {
        this.__hsCartAdd = isCartAddUrl(u) && String(m || "").toUpperCase() === "POST";
        this.__hsCartChange = !this.__hsCartAdd && isCartChangeUrl(u) && String(m || "").toUpperCase() === "POST";
        this.__hsBody = null;
        return _open.apply(this, arguments);
      };
      XHR.prototype.send = function (body) {
        if (this.__hsCartChange) {
          var rem = parseRemoval(body);
          if (rem) {
            var xr = this;
            this.addEventListener("loadend", function () {
              if (xr.status >= 200 && xr.status < 300) {
                onCartRemove(rem.ids); onCartUpdate(rem.changes);
                try { var cb = JSON.parse(xr.responseText); if (cb && Array.isArray(cb.items)) rememberLines(cb.items, true); } catch (_) {}
              }
            });
          }
        }
        if (this.__hsCartAdd) {
          var p = parseAddBody(body);
          var xhr = this;
          this.addEventListener("loadend", function () {
            // loadend fires on success, HTTP error, network error and abort
            // alike; status is 0 for the latter two — count 2xx only.
            if (xhr.status < 200 || xhr.status >= 300) return;
            /* Unlike a fetch Response, responseText is a plain string and can
               be read as often as we like — no clone needed, nothing consumed. */
            var items = null;
            try {
              var ab = JSON.parse(xhr.responseText);
              try { rememberLines(ab && Array.isArray(ab.items) ? ab.items : (ab ? [ab] : [])); } catch (_) {}
              items = normaliseAddedItems(ab);
            } catch (_) {}
            onCartAdd(p.variantId, p.qty, items);
          });
        }
        return _sendX.apply(this, arguments);
      };
    }
  } catch (_) {}

  // ── Purchase self-fire (GoKwik-proof, mobile-proof) ─────────────────────────
  // GoKwik completes the order in its overlay, then redirects mobile buyers to
  // the accounts subdomain where no merchant tag can run — so purchase tracking
  // that waits for a thank-you page never fires for most buyers. The reliable
  // moment is HERE: GoKwik announces order success to this parent page
  // (postMessage / Shopify.analytics.publish / dataLayer) BEFORE the redirect.
  // We listen on all three and fire the purchase tracking ourselves. GA4 and
  // Google Ads both dedup on transaction_id, so double-signals are safe.

  // Two-pass order-id resolution. Google/GA4 dedup is exact STRING equality, and
  // the Shopify channel pixel (desktop order-status page) + our server-side join
  // both key on the NUMERIC Shopify order id — so prefer that; only fall back to
  // the order NAME (with a leading '#' stripped) when no numeric id is present.
  var ORDER_ID_NUM_RE = /^(moid|order.?id|merchant.?order.?id|transaction.?id)$/i;
  var ORDER_KEY_RE = /(order.?(name|id|number)|merchant.?order.?id|transaction.?id)/i;
  // Two-pass ORDER-TOTAL resolution, for the same reason as the order id above.
  // GoKwik's COD success payload carries BOTH the order total AND the small COD
  // verification charge (`amount: 5`). A single regex + first-match-wins deepFind
  // returned whichever key the object happened to iterate first — which for COD
  // flows was the ₹5 charge, so the GA4/Ads valued conversion fired at ₹5 instead
  // of the real basket. Worse, Ads dedups per (action, transaction_id) first-in-
  // wins, so the ₹5 self-fire also BLOCKED the correctly-valued desktop channel
  // pixel for the same order. Prefer unambiguous order-total keys; only fall back
  // to the generic `amount`/`value` when no specific key exists.
  // ⚠ UNIT TRAP (verified on live data 2026-07-28): `total_price` is AMBIGUOUS —
  // GoKwik's Order_Placed_GK event sends it in RUPEES ("569.05") while Shopify's
  // cart.js sends the SAME KEY in PAISE (a ₹1,120.05 cart carries 112005). Taking
  // it at face value reported values 100× too high. It is handled separately by
  // normalizeAmount() below; every key in TOTAL_SPECIFIC_RE is verified rupees.
  var TOTAL_SPECIFIC_RE = /^(order_total|total_cart_amount|grand_total|totalValue|order_amount|total)$/i;
  var TOTAL_AMBIGUOUS_RE = /^(total_price)$/i;

  // Paise are always integers; rupee totals from these sources carry decimals.
  // An integer at or above the paise threshold is divided down; anything still
  // implausible is discarded rather than reported.
  function normalizeAmount(raw) {
    var str = String(raw).trim();
    var n = parseFloat(str);
    if (!isFinite(n) || n <= 0) return null;
    var isInteger = str.indexOf(".") === -1;
    if (isInteger && n >= 10000) n = n / 100; // paise → rupees
    return n;
  }
  var TOTAL_GENERIC_RE = /^(value|amount)$/i;
  // Absolute backstop: this store's AOV is ~₹1,000-1,800. Any resolved "total"
  // above this ceiling is a unit error (paise) or the wrong field — never a real
  // basket. Reporting nothing is strictly safer than reporting a 100× value:
  // an inflated conversion actively misteaches value-based bidding.
  var MAX_PLAUSIBLE_TOTAL = 25000;   // clears any plausible basket at AOV ₹1,000-1,800
  var MIN_PLAUSIBLE_TOTAL = 50;

  function deepFind(obj, keyRe, want, depth) {
    if (!obj || typeof obj !== "object" || depth > 4) return null;
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (keyRe.test(k)) {
        if (want === "num") {
          var n = parseFloat(v);
          if (isFinite(n) && n > 0 && n < 10000000) return n;
        } else if (v != null && (typeof v === "string" || typeof v === "number") && String(v).length > 0 && String(v).length < 64) {
          return String(v);
        }
      }
      if (v && typeof v === "object") {
        var r = deepFind(v, keyRe, want, depth + 1);
        if (r != null) return r;
      }
    }
    return null;
  }

  // Numeric variant of deepFind that returns the LARGEST match rather than the
  // first. Order-success payloads nest several plausible numbers (line-item price,
  // shipping, COD fee, basket total); the basket total is the largest of them.
  function deepFindMax(obj, keyRe, depth, normalise) {
    if (!obj || typeof obj !== "object" || depth > 4) return null;
    var best = null;
    for (var k in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
      var v = obj[k];
      if (keyRe.test(k)) {
        var n = normalise ? normalizeAmount(v) : parseFloat(v);
        if (n !== null && isFinite(n) && n >= MIN_PLAUSIBLE_TOTAL && n <= MAX_PLAUSIBLE_TOTAL && (best === null || n > best)) best = n;
      }
      if (v && typeof v === "object") {
        var r = deepFindMax(v, keyRe, depth + 1, normalise);
        if (r != null && (best === null || r > best)) best = r;
      }
    }
    return best;
  }

  function looksLikeOrderSuccess(name) {
    return /order.?placed|gk_purchase|gokwik_purchase|^purchase$|purchase_gk|order.?success|order.?confirm/i.test(String(name || ""));
  }

  var purchaseFired = {};
  function onOrderSuccess(payload, source) {
    try {
      var oid = deepFind(payload, ORDER_ID_NUM_RE, "str", 0) || deepFind(payload, ORDER_KEY_RE, "str", 0);
      if (oid) oid = String(oid).replace(/^#/, "");
      // Specific keys first; generic (amount/value) only as a fallback. Both passes
      // collect the MAXIMUM match rather than the first, because a payload can carry
      // several same-named numbers at different depths (line-item price vs basket).
      // Trusted rupee keys → unit-ambiguous key (normalised) → generic last.
      var total = deepFindMax(payload, TOTAL_SPECIFIC_RE, 0);
      if (!total) total = deepFindMax(payload, TOTAL_AMBIGUOUS_RE, 0, true);
      if (!total) total = deepFindMax(payload, TOTAL_GENERIC_RE, 0, true);
      // Final guard at the fire site: never report an implausible value to Google.
      if (total && (total < MIN_PLAUSIBLE_TOTAL || total > MAX_PLAUSIBLE_TOTAL)) total = null;
      // Value-aware latch. GoKwik emits several success signals per order and only
      // some payloads carry the total: a valueless one must NOT lock out the valued
      // one that follows. "1" = order_placed only, "2" = valued gtag fired (final).
      var key = "_hs_pf_" + (oid || "session");
      var prev = purchaseFired[key] || "";
      try { prev = prev || sessionStorage.getItem(key) || ""; } catch (_) {}
      var canValue = Boolean(total) && typeof window.gtag === "function" && Boolean(window.HS_GA4_ID || window.HS_ADS_SEND_TO);
      if (prev === "2" || (prev === "1" && !canValue)) return;
      var state = canValue ? "2" : "1";
      purchaseFired[key] = state;
      try { sessionStorage.setItem(key, state); } catch (_) {}
      // 1. GA4 purchase with real value (GA4 drops later purchases w/ same transaction_id)
      try {
        if (typeof window.gtag === "function" && window.HS_GA4_ID && total) {
          window.gtag("event", "purchase", {
            send_to: window.HS_GA4_ID,
            transaction_id: oid || undefined,
            value: total,
            currency: "INR",
          });
        }
      } catch (_) {}
      // 2. Google Ads valued conversion (dedups on transaction_id per action —
      //    safe alongside the desktop order-status-page channel-pixel conversion)
      try {
        if (typeof window.gtag === "function" && window.HS_ADS_SEND_TO && total) {
          window.gtag("event", "conversion", {
            send_to: window.HS_ADS_SEND_TO,
            transaction_id: oid || undefined,
            value: total,
            currency: "INR",
          });
        }
      } catch (_) {}
      // 3. Our own stream (server writes the attribution bridge) — first fire only,
      //    so a valueless→valued upgrade doesn't emit a duplicate order_placed.
      if (prev !== "") return;
      send(Object.assign({}, base, {
        event_type: "order_placed",
        page_url: location.href,
        order_id: oid || undefined,
        order_total: total || undefined,
        metadata: { signal_source: source || null },
      }));
    } catch (_) {}
  }

  // Signal 1: window messages (GoKwik iframe → parent). Shapes vary; match by name.
  window.addEventListener("message", function (e) {
    try {
      var d = e.data;
      if (!d) return;
      var name = null, payload = d;
      if (typeof d === "object") {
        name = d.eventName || d.eventname || d.event || d.name || null;
        if (!name && d.data && typeof d.data === "object") name = d.data.eventname || d.data.eventName || d.data.event || null;
        if (d.type === "gokwik_events" && d.data) payload = d.data;
        // Structural signal: {type:"orderSuccess", response:{…}} — GoKwik's own
        // republish trigger, independent of the analytics-pipeline event names.
        if (!name && typeof d.type === "string") { name = d.type; payload = d.response || d.data || d; }
      } else if (typeof d === "string") {
        name = d;
      }
      if (name && looksLikeOrderSuccess(name)) onOrderSuccess(payload, "message:" + name);
      else if (name) onCheckoutStep(name, e, d);
    } catch (_) {}
  });
  /* GoKwik checkout STEPS between checkout_start and order_placed (phone entered, OTP, address,
     payment method, payment failed …). We do not know GoKwik's full vocabulary, so every message
     NAME from a GoKwik origin (or a gokwik_events envelope) is recorded once per session as
     checkout_step {step}. Never the payload — it can carry the customer's phone or address. */
  /* GoKwik SDK plumbing, not shopper steps (all observed on staging 2026-10-23). */
  var STEP_NOISE = /^(check_core_token|clearGokwikIframeInterval|trackEvents|merchantInfo|gk_sdk_event|gokwik_events|checkout_assets_loaded|checkout_event|new_ui_shown|user_experiments_fetched|screen_\w+|trackFb\w*|iframe\w*|ping|pong|heartbeat|resize\w*|ready|\w*_cnt|\w*_count|kp_vst\w*)$/i;  // + KwikPass visit counters (kp_vst_cnt, seen live 2026-10-23)
  /* A step NAME must look like an identifier: letter first, 3–49 word chars, and no run of 4+
     digits — names are not a fixed vocabulary (GoKwik sends e.g. screen_width_599), so a name
     carrying a phone number, an OTP or a person's name must be impossible to record. */
  var STEP_OK = /^[A-Za-z][A-Za-z0-9_]{2,48}$/;
  var GK_ORIGIN = /^https:\/\/([a-z0-9-]+\.)*gokwik\.(co|in)$/i;
  function safeStepName(d) {
    if (!d || typeof d !== "object") return null;            // never a whole string message
    var dd = d.data && typeof d.data === "object" ? d.data : null;
    var n = d.eventName || d.eventname || d.event || (dd && (dd.eventname || dd.eventName || dd.event)) || d.type;
    if (typeof n !== "string") return null;
    if (/^gk_sdk_event$/i.test(n)) {                           // envelope → inner event name only
      n = dd && (dd.eventname || dd.eventName || dd.event);
      if (typeof n !== "string") return null;
    }
    if (!STEP_OK.test(n) || /\d{4,}/.test(n) || STEP_NOISE.test(n)) return null;
    return n;
  }
  var _stepsSeen = {}, _stepAttempt = 1;
  try {
    var _ss = JSON.parse(sessionStorage.getItem("_hs_ck_steps") || "{}") || {};
    _stepsSeen = _ss.seen || {}; _stepAttempt = _ss.attempt || 1;
  } catch (_) {}
  var _stepsThisPage = 0;
  function onCheckoutStep(name, ev, d) {
    try {
      var origin = String((ev && ev.origin) || "");
      // Exact GoKwik hosts, or GoKwik's envelope relayed inside OUR page (the side cart).
      var fromGokwik = GK_ORIGIN.test(origin) ||
        (origin === location.origin && d && typeof d === "object" && d.type === "gokwik_events");
      if (!fromGokwik) return;
      var step = safeStepName(d);
      if (!step) return;
      // A new checkout opening starts a new ATTEMPT, so a retry after a failed payment is visible.
      if (step === "checkout_initiated" && _stepsSeen.checkout_initiated) { _stepsSeen = {}; _stepAttempt++; }
      if (_stepsSeen[step] || _stepsThisPage >= 30) return;
      _stepsSeen[step] = 1; _stepsThisPage++;
      try { sessionStorage.setItem("_hs_ck_steps", JSON.stringify({ seen: _stepsSeen, attempt: _stepAttempt })); } catch (_) {}
      send(Object.assign({}, base, { event_type: "checkout_step", page_url: location.href,
        metadata: { step: step, attempt: _stepAttempt, origin: origin.slice(0, 80) } }));
    } catch (_) {}
  }

  // Signal 2: Shopify.analytics.publish — GoKwik publishes gokwik_purchase_event /
  // gokwik_events into Shopify's pixel bus; observe by wrapping publish.
  (function wrapPublish(tries) {
    try {
      var sa = window.Shopify && window.Shopify.analytics;
      if (sa && typeof sa.publish === "function" && !sa.__hsWrapped) {
        var orig = sa.publish;
        sa.publish = function (name, payload) {
          try {
            if (looksLikeOrderSuccess(name)) onOrderSuccess(payload, "publish:" + name);
            else if (/gokwik/i.test(String(name || ""))) {
              var inner = deepFind(payload, /^(eventname|eventName|name|event|stepName)$/i, "str", 0);
              if (looksLikeOrderSuccess(inner)) onOrderSuccess(payload, "publish:" + name + ":" + inner);
            }
          } catch (_) {}
          return orig.apply(this, arguments);
        };
        sa.__hsWrapped = true;
        return;
      }
    } catch (_) {}
    if ((tries || 0) < 60) setTimeout(function () { wrapPublish((tries || 0) + 1); }, 500);
  })(0);

  // ── GoKwik dataLayer bridge ─────────────────────────────────────────────────
  // GoKwik pushes checkout-initiated / order-placed / gk_purchase to dataLayer.
  // Mirror them into our stream (and re-assert cart attributes at checkout) —
  // this is the deterministic checkout/purchase signal, replacing DOM guessing
  // and the Shopify thank-you page that never renders under GoKwik.

  /* checkout_start has fired ONCE in seven weeks of live traffic against ~1,000
     orders a month. Both of its triggers assume things that are not true under
     GoKwik: the dataLayer matcher assumes GoKwik pushes `checkout-initiated`,
     and the click matcher assumes the checkout button is Shopify's own markup —
     but GoKwik injects its OWN side cart, so neither matches.

     Rather than guess GoKwik's event name, the matcher below is deliberately
     BROAD (anything whose name mentions checkout) with an explicit exclusion
     list for the terminal events, and every emission records WHICH path fired
     in metadata.trigger. That turns "why doesn't it fire" from a debugging
     session into a query: once real traffic lands, `metadata->>'trigger'` says
     which door shoppers actually come through, and the losing paths can be
     dropped. A heuristic that reports its own provenance is honest; a hardcoded
     guess that fails silently is what we already had. */
  var lastCheckoutStart = 0;
  function emitCheckoutStart(trigger) {
    /* One intent, one event. The click and dataLayer paths can BOTH fire for a
       single tap, and a shopper bouncing off the checkout overlay can tap
       again; 30s collapses those without hiding a genuine second attempt. */
    var now = Date.now();
    if (now - lastCheckoutStart < 30000) return;
    lastCheckoutStart = now;
    setCartAttributes(true); // last chance before the GoKwik overlay takes over
    send(Object.assign({}, base, {
      event_type: "checkout_start",
      page_url: location.href,
      metadata: { trigger: String(trigger).slice(0, 80) },
    }));
  }

  function looksLikeCheckoutStart(evt) {
    if (typeof evt !== "string" || !evt) return false;
    var e = evt.toLowerCase();
    if (!/checkout/.test(e)) return false;
    /* Exclude the END of checkout — "checkout_completed" contains "checkout"
       and would otherwise be counted as a START, inflating the numerator of
       every funnel rate that uses it. */
    return !/(complete|success|purchase|order|placed|thank)/.test(e);
  }

  function handleDlEvent(o) {
    if (!o) return;
    var evt = o.event || (Array.isArray(o) ? o[0] : null);
    if (looksLikeCheckoutStart(evt)) {
      emitCheckoutStart("dataLayer:" + evt);
    } else if (evt === "order-placed" || evt === "gk_purchase" || evt === "purchase") {
      onOrderSuccess(o, "dataLayer:" + evt);
    }
  }
  try {
    window.dataLayer = window.dataLayer || [];
    // Replay anything already pushed before we loaded.
    for (var i = 0; i < window.dataLayer.length; i++) { try { handleDlEvent(window.dataLayer[i]); } catch (_) {} }
    var _dlPush = window.dataLayer.push;
    window.dataLayer.push = function () {
      for (var j = 0; j < arguments.length; j++) { try { handleDlEvent(arguments[j]); } catch (_) {} }
      return _dlPush.apply(this, arguments);
    };
  } catch (_) {}

  // ── Native-checkout fallback (harmless under GoKwik) ────────────────────────
  // Hidden note_attributes on any Shopify /checkout form, for the rare native
  // path. GoKwik uses its own overlay, so this simply no-ops there.
  function injectCheckoutInputs() {
    try {
      var forms = document.querySelectorAll("form[action*='/checkout']");
      var click = storedClick();
      var fields = [
        ["utm_source", sess.utm_source], ["utm_medium", sess.utm_medium],
        ["utm_campaign", sess.utm_campaign], ["utm_content", sess.utm_content],
        ["utm_term", sess.utm_term], ["hs_session_id", sess.id], ["hs_anon_id", anonId],
        ["hs_gclid", click && click.gclid], ["hs_gbraid", click && click.gbraid],
        ["hs_wbraid", click && click.wbraid], ["hs_fbclid", click && click.fbclid],
      ];
      forms.forEach(function (form) {
        fields.forEach(function (pair) {
          if (!pair[1]) return;
          var nm = "attributes[" + pair[0] + "]";
          if (form.querySelector("input[name='" + nm + "']")) return;
          var inp = document.createElement("input");
          inp.type = "hidden"; inp.name = nm; inp.value = pair[1];
          form.appendChild(inp);
        });
      });
    } catch (_) {}
  }
  injectCheckoutInputs();
  var CHECKOUT_SELECTOR = [
    "a[href*='/checkout']",
    "button[name='checkout']",
    "[data-gokwik]",
    ".gokwik-checkout-btn",
    /* GoKwik's own injected side cart — its markup is not Shopify's, which is
       why the original four selectors never matched. These are additive and
       cost nothing when absent. */
    "[class*='gokwik'][class*='checkout']",
    "[id*='gokwik'][id*='checkout']",
    "[data-gk-checkout]",
    ".gk-checkout-btn",
  ].join(", ");
  document.addEventListener("click", function (e) {
    var el = e.target && e.target.closest ? e.target.closest(CHECKOUT_SELECTOR) : null;
    if (!el) return;
    /* Name the matching rule, not just "click" — with several selectors in play
       we need to know WHICH one caught it to prune the list later. */
    var which = "click";
    try {
      for (var i = 0, parts = CHECKOUT_SELECTOR.split(", "); i < parts.length; i++) {
        if (el.matches(parts[i])) { which = "click:" + parts[i]; break; }
      }
    } catch (_) {}
    emitCheckoutStart(which);
    injectCheckoutInputs();
  }, true);

  /* ── checkout_start, observed from the CONSEQUENCE ────────────────────────
     Both paths above need GoKwik to ANNOUNCE itself: a dataLayer push we can
     pattern-match, or a click on markup we can select. Thirty days of live
     traffic says neither happens — 5,220 sessions added to cart, 1,209
     ordered, checkout_start fired ZERO times. Broadening the selector list
     again would be the third guess at the same unknown.

     ⚠ The likeliest reason the click path cannot work is structural, not a
     wrong selector: a click inside an IFRAME never reaches this document's
     listener, in capture phase or otherwise. If GoKwik renders its cart in an
     iframe, no selector can ever match, because the event does not exist in
     our document.

     So the two paths below watch what checkout DOES:
       (1) the GoKwik surface becoming VISIBLE — the desktop overlay;
       (2) the page unloading moments after a plausible CTA click while the
           cart holds items — the MOBILE redirect to the accounts subdomain,
           which this theme's own notes call the majority of buyers and which
           produces no overlay at all.

     Both route through emitCheckoutStart(), so its 30s dedupe keeps one intent
     to one event however many paths notice it, and both name themselves in
     metadata.trigger. Path 2 carries a SIGNATURE of the clicked element, which
     turns "we do not know what GoKwik's button looks like" from a blocker into
     a query against live rows. */

  /** Short, bounded description of an element: enough to identify a button. */
  function elementSignature(el) {
    try {
      if (!el || !el.tagName) return "?";
      var sig = el.tagName.toLowerCase();
      if (el.id) sig += "#" + String(el.id).slice(0, 24);
      var cls = typeof el.className === "string" ? el.className : "";
      if (cls) sig += "." + cls.trim().split(/\s+/).slice(0, 2).join(".").slice(0, 32);
      /* Redact digit runs before they reach metadata.trigger. This is an
         analytics field on a storefront with no consent banner; a surface
         rendering a phone number or order id inline would otherwise put it in
         our event stream. Cheap, and costs nothing we want. */
      var txt = (el.textContent || "").trim().replace(/\s+/g, " ").replace(/\d{4,}/g, "#").slice(0, 24);
      if (txt) sig += "|" + txt;
      return sig.slice(0, 80);
    } catch (_) { return "?"; }
  }

  /* Cart size, kept fresh enough to read synchronously at unload. `null` means
     "not known yet" and is treated as NOT eligible — a funnel numerator that
     guesses is worse than one that undercounts. */
  var cartItemCount = null;
  function refreshCartCount() {
    try {
      _origFetch("/cart.js", { credentials: "same-origin" })
        .then(function (r) { return r.json(); })
        .then(function (c) { if (c && typeof c.item_count === "number") cartItemCount = c.item_count; })
        .catch(function () {});
    } catch (_) {}
  }
  refreshCartCount();

  /* Deliberately broad: it must catch a button whose label we have never seen.
     Narrow enough to exclude ordinary navigation ("Rings", "About us"), which
     is what stops path 2 firing on every exit. */
  var CTA_TEXT = /checkout|buy now|buy it now|place order|proceed|continue to pay|pay now|order now/i;
  var lastCta = { at: 0, sig: "" };
  document.addEventListener("click", function (e) {
    try {
      var el = e.target && e.target.closest ? e.target.closest("a,button,[role='button'],input[type='submit']") : null;
      if (!el) return;
      var hay = ((el.textContent || "") + " " + (el.getAttribute && (el.getAttribute("name") || "") ) + " " +
                 (el.getAttribute && (el.getAttribute("aria-label") || ""))).slice(0, 200);
      if (!CTA_TEXT.test(hay)) return;
      lastCta = { at: Date.now(), sig: elementSignature(el) };
      refreshCartCount();
    } catch (_) {}
  }, true);

  /* (1) The overlay. Fires on VISIBLE, not on inserted: GoKwik may place a
     hidden container at page load, and firing on insertion would report a
     checkout start for every pageview — a false positive far worse than the
     zero we have now. */
  /* An overlay built by assigning innerHTML on a wrapper arrives as a SINGLE
     mutation record whose only addedNode is that wrapper. If the wrapper's own
     class does not mention GoKwik, checking the node alone misses the match
     entirely -- so check the node AND its descendants. */
  function matchInTree(node) {
    var sig = looksLikeGokwikSurface(node);
    if (sig) return sig;
    try {
      if (!node || node.nodeType !== 1 || !node.querySelectorAll) return null;
      /* ⚠ Not a tag allowlist. GoKwik's inner markup may be a <dialog>,
         <aside> or a custom element, and a hardcoded list silently misses it.
         Scanning every descendant is bounded by the cap below, and the per-node
         test is a cheap early-exit regex. */
      var inner = node.querySelectorAll("*");
      for (var k = 0; k < inner.length && k < 200; k++) {
        var s2 = looksLikeGokwikSurface(inner[k]);
        if (s2) return s2;
      }
    } catch (_) {}
    return null;
  }

  function looksLikeGokwikSurface(node) {
    try {
      if (!node || node.nodeType !== 1) return null;
      var id = String(node.id || "");
      var cls = typeof node.className === "string" ? node.className : "";
      var src = node.tagName === "IFRAME" && node.getAttribute ? String(node.getAttribute("src") || "") : "";
      var hay = id + " " + cls + " " + src;
      /* ⚠ NOT a bare /kwik/. GoKwik ships more than checkout: KwikPass (OTP
         login) and KwikEngage (the WhatsApp widget, in active use here) both
         render >=200x200 surfaces whose class/id contains "kwik". Matching
         those would emit checkout_start for a shopper who never touched
         checkout AND -- because this observer disconnects on first match --
         make path 1 miss the REAL overlay for the rest of the pageview. One
         false positive would cost both a bad row and the feature's coverage. */
      /* ⚠ The exclusion yields to an explicit "checkout". GoKwik namespaces its
         own surfaces, so a KwikPass login may well be `gokwik-kwikpass-*` —
         which the inclusion below WOULD match, so the exclusion is genuinely
         load-bearing. But a checkout overlay that embeds an OTP step could be
         `gokwik-checkout-kwikpass-*`, and excluding that would convert a fix
         for a false positive into a false NEGATIVE. "checkout" decides. */
      if (!/checkout/i.test(hay) && /kwik-?pass|kwik-?engage/i.test(hay)) return null;
      if (!/gokwik|gk-?checkout|gkcheckout/i.test(hay)) return null;
      var r = node.getBoundingClientRect ? node.getBoundingClientRect() : null;
      // A real checkout surface occupies the screen. A tracking pixel or a
      // collapsed container does not.
      if (!r || r.width < 200 || r.height < 200) return null;
      return elementSignature(node);
    } catch (_) { return null; }
  }
  try {
    if (typeof MutationObserver === "function" && document.body) {
      var mo = new MutationObserver(function (records) {
        /* ⚠ The setup try/catch below wraps only `new MutationObserver()` and
           `.observe()`. THIS function runs later, on its own stack -- an
           exception here is uncaught, reaches the console, and skips the
           disconnect below, leaving the observer attached for the rest of the
           pageview. Path 2 already guards its emit; this matches it. */
        try {
        for (var i = 0; i < records.length; i++) {
          var rec = records[i];
          var candidates = rec.type === "attributes" ? [rec.target] : (rec.addedNodes || []);
          for (var j = 0; j < candidates.length; j++) {
            var sig = matchInTree(candidates[j]);
            if (sig) {
              emitCheckoutStart("overlay:" + sig);
              /* Its job for this pageview is done. childList+subtree on <body>
                 fires for every DOM change on a storefront full of carousels
                 and lazy images; leaving it attached spends CPU on every one
                 of them for a signal we already have. */
              try { mo.disconnect(); } catch (_) {}
              return;
            }
          }
        }
        } catch (_) { /* tracking must never surface an error on the storefront */ }
      });
      mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["style", "class", "hidden"] });
    }
  } catch (_) {}

  /* (2) The mobile redirect. No overlay ever appears, so the only observable
     is that we are leaving right after a checkout CTA with a non-empty cart.
     `pagehide` (not `beforeunload`) because bfcache-eligible navigations and
     mobile Safari fire it reliably; send() already sets keepalive so the
     request survives the unload. */
  /* ⚠ visibilitychange FIRST, pagehide only as a backstop. Measured browser
     behaviour, not preference: `pagehide` is not guaranteed to fire, and mobile
     Safari drops in-flight requests when a navigation commits quickly — with
     sendBeacon AND with fetch keepalive alike. Since this path exists
     specifically for the MOBILE redirect, relying on unload alone would make it
     least reliable exactly where it matters most. `visibilitychange → hidden`
     fires BEFORE the navigation commits, and the documented-reliable pattern is
     both together (~91%).

     Firing twice is harmless: emitCheckoutStart()'s 30s dedupe collapses them.
     A tab-switch after a checkout tap also counts as a checkout start, which is
     correct — the shopper did begin checkout. */
  function exitCheckoutProbe(why) {
    try {
      if (!lastCta.at || Date.now() - lastCta.at > 2500) return;
      if (!cartItemCount) return;   // null or 0 — both ineligible
      emitCheckoutStart(why + ":" + lastCta.sig);
    } catch (_) {}
  }
  document.addEventListener("visibilitychange", function () {
    try { if (document.visibilityState === "hidden") exitCheckoutProbe("hidden"); } catch (_) {}
  });
  window.addEventListener("pagehide", function () { exitCheckoutProbe("unload"); });

  /* ── section real-estate telemetry (2026-09-25) ────────────────────────────
     Answers "which parts of a page do people actually use, and which are dead
     space", so sections can be reordered or removed on evidence.

     ⚠ CLICKS ALONE CANNOT ANSWER THAT. A section low on the page gets few
     clicks because few people ever see it, not because it is bad. So this
     records VIEWS as well, and the metric that matters is clicks ÷ views.

     Rides on event_type "engagement", which is already in the DB CHECK
     constraint, so this needs no migration. Detail goes in metadata (jsonb).

     Cost control: ONE delegated listener + ONE IntersectionObserver, both
     passive. The shared once() ledger has a hard 200-event page ceiling that
     protects add_to_cart and friends, so this keeps its OWN much smaller caps
     and can never crowd them out.

     Privacy: records the section id, the kind of element, and the KIND of
     destination. Never link text, never input values, never coordinates, never
     a full URL with query parameters. Nothing here is personal data.

     Kill switch: window.HS_SECTION_TRACKING = false (before this script). */
  (function () {
    try {
      if (window.HS_SECTION_TRACKING === false) return;
      var VIEW_CAP = 10, CLICK_CAP = 10, views = 0, clicks = 0;

      /* Section ids look like shopify-section-template--123456__product_carousel.
         The template number changes whenever the theme is duplicated, so it is
         stripped: the part after "__" is the stable, human-readable name. */
      function sectionOf(node) {
        var el = node;
        while (el && el !== document.body) {
          if (el.id && el.id.indexOf("shopify-section") === 0) {
            var raw = el.id.replace(/^shopify-section-/, "");
            var name = raw.indexOf("__") > -1 ? raw.split("__").pop() : raw.replace(/^template--\d+-?/, "");
            name = String(name || raw);
            /* A section added in the theme editor without a name gets a generated
               hex id. Label it as unnamed rather than letting a meaningless hex
               string look like a real section name in the analysis. */
            if (/^[0-9a-f]{12,}$/i.test(name)) name = "unnamed_" + name.slice(0, 6);
            return { id: el, name: name.slice(0, 48) };
          }
          el = el.parentElement;
        }
        return null;
      }
      function indexOfSection(el) {
        var all = document.querySelectorAll('[id^="shopify-section"]');
        for (var i = 0; i < all.length; i++) if (all[i] === el) return i;
        return -1;
      }
      function emitSection(kind, name, idx, extra) {
        var meta = { kind: kind, section: name, idx: idx };
        if (pageType) meta.page_type = String(pageType).slice(0, 40);
        if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) meta[k] = extra[k];
        send(Object.assign({}, base, { event_type: "engagement", page_url: location.href, metadata: meta }));
      }

      /* VIEWS — 50% of the section on screen for 1s. The dwell requirement is
         what stops a fast scroll from marking every section as "seen". */
      if (typeof IntersectionObserver === "function") {
        var timers = {};
        var io = new IntersectionObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) {
            (function (en) {
              var s = sectionOf(en.target); if (!s) return;
              var key = s.name + "|" + indexOfSection(en.target);
              if (en.isIntersecting) {
                if (timers[key]) return;
                timers[key] = setTimeout(function () {
                  timers[key] = 0;
                  if (views >= VIEW_CAP) return;
                  if (!once("sect_view", key)) return;
                  views += 1;
                  emitSection("sect_view", s.name, indexOfSection(en.target));
                  io.unobserve(en.target);
                }, 1000);
              } else if (timers[key]) { clearTimeout(timers[key]); timers[key] = 0; }
            })(entries[i]);
          }
        }, { threshold: 0.5 });
        var secs = document.querySelectorAll('[id^="shopify-section"]');
        for (var si = 0; si < secs.length && si < 40; si++) io.observe(secs[si]);
      }

      /* CLICKS — one delegated listener. Capture phase so it still records a
         click on a control that stops propagation on its way up. */
      document.addEventListener("click", function (e) {
        try {
          if (clicks >= CLICK_CAP) return;
          var t = e.target; if (!t || t.nodeType !== 1) return;
          var s = sectionOf(t); if (!s) return;
          var idx = indexOfSection(s.id);
          var a = t.closest ? t.closest("a[href]") : null;
          var el = a ? "link" : (t.closest && t.closest("button") ? "button" : (t.tagName === "IMG" ? "img" : "other"));
          var dest = "none";
          if (a) {
            var p = "";
            try { p = new URL(a.getAttribute("href"), location.origin).pathname; } catch (_) { p = ""; }
            dest = /^\/products\//.test(p) ? "product"
                 : /^\/collections\//.test(p) ? "collection"
                 : /^\/blogs\//.test(p) ? "blog"
                 : /^\/pages\//.test(p) ? "page"
                 : /^\/cart/.test(p) ? "cart"
                 : p === "/" ? "home" : "other";
          }
          var key = s.name + "|" + idx + "|" + el + "|" + dest;
          if (!once("sect_click", key)) return;
          clicks += 1;
          emitSection("sect_click", s.name, idx, { el: el, dest: dest });
        } catch (_) {}
      }, true);
    } catch (_) {}
  })();

})();
